/**
 * Mycelium Time & Dependency Management Connector
 *
 * Coordinates time tracking, task dependencies, and workload analytics through
 * the mycelium event system, enabling emergent intelligence and holographic organization.
 *
 * Key Responsibilities:
 * - Emit time tracking events (start, stop, pause, resume)
 * - Emit dependency change events (create, update, delete, circular detection)
 * - Emit workload events (capacity changes, analytics updates)
 * - Listen for task completion events to update dependencies
 * - Listen for estimation events to recalculate critical paths
 * - Provide gravitational organization through event-driven updates
 *
 * Mycelium Principles Applied:
 * - Economy of Movement: Single event triggers cascading updates
 * - Holographic Organization: Each component reflects system state
 * - Gravitational Pull: Related tasks naturally cluster through events
 *
 * @module mycelium-time-dependency-connector
 * @since 2026-02-21
 */
import type { IService, ServiceResponse } from "./service-registry";
import type { MyceliumEvent, EventDomain, EventSubscriber, MyceliumEventBackbone, } from "./mycelium-event-backbone.service";
import { logger } from "../lib/logger";
// ============================================================================
// EVENT TYPE DEFINITIONS
// ============================================================================
/**
 * Time tracking event types
 */
export const TIME_TRACKING_EVENTS = {
    STARTED: "time_tracking_started",
    STOPPED: "time_tracking_stopped",
    PAUSED: "time_tracking_paused",
    RESUMED: "time_tracking_resumed",
    TRACKED: "time_tracked",
    SESSION_SAVED: "time_session_saved",
} as const;
/**
 * Dependency management event types
 */
export const DEPENDENCY_EVENTS = {
    CREATED: "dependency_created",
    UPDATED: "dependency_updated",
    DELETED: "dependency_deleted",
    CIRCULAR_DETECTED: "circular_dependency_detected",
    CIRCULAR_RESOLVED: "circular_dependency_resolved",
    CRITICAL_PATH_CALCULATED: "critical_path_calculated",
    SLACK_CALCULATED: "task_slack_calculated",
    GRAPH_UPDATED: "dependency_graph_updated",
} as const;
/**
 * Workload event types
 */
export const WORKLOAD_EVENTS = {
    CAPACITY_UPDATED: "workload_capacity_updated",
    THRESHOLD_EXCEEDED: "workload_threshold_exceeded",
    ANALYTICS_UPDATED: "workload_analytics_updated",
    TREND_DETECTED: "workload_trend_detected",
    PRODUCTIVITY_SCORED: "productivity_scored",
} as const;
/**
 * Task estimation event types
 */
export const ESTIMATION_EVENTS = {
    CREATED: "estimation_created",
    UPDATED: "estimation_updated",
    ACCURACY_LOGGED: "estimation_accuracy_logged",
    AI_SUGGESTED: "estimation_ai_suggested",
} as const;
// ============================================================================
// PAYLOAD INTERFACES
// ============================================================================
export interface TimeTrackingPayload {
    taskId: string;
    userId: string;
    timestamp: number;
    duration?: number; // minutes
    sessionId?: string;
    previousDuration?: number;
    isBillable?: boolean;
}
export interface DependencyPayload {
    dependencyId?: string;
    predecessorTaskId: string;
    successorTaskId: string;
    dependencyType: "finish_to_start" | "start_to_start" | "finish_to_finish" | "start_to_finish";
    lagTime?: number;
    userId: string;
    circularDependencyChain?: string[];
}
export interface WorkloadPayload {
    userId: string;
    date: string;
    plannedHours?: number;
    actualHours?: number;
    taskCount?: number;
    completedTasks?: number;
    focusTimeMinutes?: number;
    interruptionCount?: number;
    productivityScore?: number;
    capacityUtilization?: number;
    trend?: "increasing" | "decreasing" | "stable";
}
export interface EstimationPayload {
    taskId: string;
    estimatedDuration: number; // minutes
    actualDuration?: number;
    confidenceLevel: "low" | "medium" | "high";
    estimationMethod: "manual" | "ai_suggested" | "historical" | "template";
    accuracy?: number; // 0-100
    userId: string;
}
export interface CriticalPathPayload {
    taskIds: string[];
    criticalTasks: string[];
    totalDuration: number;
    earliestCompletion: string;
    userId: string;
}
// ============================================================================
// SERVICE INTERFACE
// ============================================================================
export interface IMyceliumTimeDependencyConnector extends IService {
    /**
     * Publish time tracking event
     */
    publishTimeTrackingEvent(eventType: (typeof TIME_TRACKING_EVENTS)[keyof typeof TIME_TRACKING_EVENTS], payload: TimeTrackingPayload): Promise<ServiceResponse<void>>;
    /**
     * Publish dependency event
     */
    publishDependencyEvent(eventType: (typeof DEPENDENCY_EVENTS)[keyof typeof DEPENDENCY_EVENTS], payload: DependencyPayload): Promise<ServiceResponse<void>>;
    /**
     * Publish workload event
     */
    publishWorkloadEvent(eventType: (typeof WORKLOAD_EVENTS)[keyof typeof WORKLOAD_EVENTS], payload: WorkloadPayload): Promise<ServiceResponse<void>>;
    /**
     * Publish estimation event
     */
    publishEstimationEvent(eventType: (typeof ESTIMATION_EVENTS)[keyof typeof ESTIMATION_EVENTS], payload: EstimationPayload): Promise<ServiceResponse<void>>;
    /**
     * Subscribe to time tracking events
     */
    subscribeToTimeTracking(handler: (event: MyceliumEvent) => Promise<void> | void, subscriberName: string): Promise<ServiceResponse<void>>;
    /**
     * Subscribe to dependency events
     */
    subscribeToDependencies(handler: (event: MyceliumEvent) => Promise<void> | void, subscriberName: string): Promise<ServiceResponse<void>>;
    /**
     * Subscribe to workload events
     */
    subscribeToWorkload(handler: (event: MyceliumEvent) => Promise<void> | void, subscriberName: string): Promise<ServiceResponse<void>>;
}
// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================
export class MyceliumTimeDependencyConnector implements IMyceliumTimeDependencyConnector {
    public readonly name = "MyceliumTimeDependencyConnector";
    private eventBackboneService?: MyceliumEventBackbone; // Will be injected
    private initialized = false;
    async initialize(): Promise<void> {
        if (this.initialized) {
            logger.warn("MyceliumTimeDependencyConnector already initialized");
            return;
        }
        try {
            // Event backbone service will be set via dependency injection
            // Register our enrichers and routers here if needed
            this.initialized = true;
            logger.info("MyceliumTimeDependencyConnector initialized successfully");
        }
        catch (error) {
            logger.error("Failed to initialize MyceliumTimeDependencyConnector", {
                error,
            });
            throw error;
        }
    }
    async dispose(): Promise<void> {
        if (!this.initialized) {
            return;
        }
        try {
            // Clean up subscriptions if needed
            this.initialized = false;
            logger.info("MyceliumTimeDependencyConnector disposed successfully");
        }
        catch (error) {
            logger.error("Failed to dispose MyceliumTimeDependencyConnector", {
                error,
            });
            throw error;
        }
    }
    /**
     * Set the event backbone service (dependency injection)
     */
    setEventBackbone(eventBackbone: MyceliumEventBackbone): void {
        this.eventBackboneService = eventBackbone;
    }
    // ========================================================================
    // PUBLISH METHODS
    // ========================================================================
    async publishTimeTrackingEvent(eventType: (typeof TIME_TRACKING_EVENTS)[keyof typeof TIME_TRACKING_EVENTS], payload: TimeTrackingPayload): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const event: Omit<MyceliumEvent, "id" | "timestamp" | "metadata"> = {
                domain: "task" as EventDomain,
                type: eventType,
                version: 1 as const,
                userId: payload.userId,
                payload: payload as unknown as Record<string, unknown>,
            };
            await this.eventBackboneService.publish(event);
            logger.info(`Published time tracking event: ${eventType}`, {
                taskId: payload.taskId,
                userId: payload.userId,
                duration: payload.duration,
            });
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to publish time tracking event", {
                eventType,
                error,
            });
            return { success: false, error: error instanceof Error ? error.message : "Failed to publish event", code: "event_publish_failed", service: this.name };
        }
    }
    async publishDependencyEvent(eventType: (typeof DEPENDENCY_EVENTS)[keyof typeof DEPENDENCY_EVENTS], payload: DependencyPayload): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const event: Omit<MyceliumEvent, "id" | "timestamp" | "metadata"> = {
                domain: "task" as EventDomain,
                type: eventType,
                version: 1 as const,
                userId: payload.userId,
                payload: payload as unknown as Record<string, unknown>,
            };
            await this.eventBackboneService.publish(event);
            logger.info(`Published dependency event: ${eventType}`, {
                predecessorTaskId: payload.predecessorTaskId,
                successorTaskId: payload.successorTaskId,
                dependencyType: payload.dependencyType,
            });
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to publish dependency event", { eventType, error });
            return { success: false, error: error instanceof Error ? error.message : "Failed to publish event", code: "event_publish_failed", service: this.name };
        }
    }
    async publishWorkloadEvent(eventType: (typeof WORKLOAD_EVENTS)[keyof typeof WORKLOAD_EVENTS], payload: WorkloadPayload): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const event: Omit<MyceliumEvent, "id" | "timestamp" | "metadata"> = {
                domain: "workload" as EventDomain,
                type: eventType,
                version: 1 as const,
                userId: payload.userId,
                payload: payload as unknown as Record<string, unknown>,
            };
            await this.eventBackboneService.publish(event);
            logger.info(`Published workload event: ${eventType}`, {
                userId: payload.userId,
                date: payload.date,
                productivityScore: payload.productivityScore,
            });
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to publish workload event", { eventType, error });
            return { success: false, error: error instanceof Error ? error.message : "Failed to publish event", code: "event_publish_failed", service: this.name };
        }
    }
    async publishEstimationEvent(eventType: (typeof ESTIMATION_EVENTS)[keyof typeof ESTIMATION_EVENTS], payload: EstimationPayload): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const event: Omit<MyceliumEvent, "id" | "timestamp" | "metadata"> = {
                domain: "task" as EventDomain,
                type: eventType,
                version: 1 as const,
                userId: payload.userId,
                payload: payload as unknown as Record<string, unknown>,
            };
            await this.eventBackboneService.publish(event);
            logger.info(`Published estimation event: ${eventType}`, {
                taskId: payload.taskId,
                estimatedDuration: payload.estimatedDuration,
                confidenceLevel: payload.confidenceLevel,
            });
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to publish estimation event", { eventType, error });
            return { success: false, error: error instanceof Error ? error.message : "Failed to publish event", code: "event_publish_failed", service: this.name };
        }
    }
    // ========================================================================
    // SUBSCRIBE METHODS
    // ========================================================================
    async subscribeToTimeTracking(handler: (event: MyceliumEvent) => Promise<void> | void, subscriberName: string): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const subscriber: EventSubscriber = {
                name: subscriberName,
                domains: ["task"],
                eventTypes: Object.values(TIME_TRACKING_EVENTS),
                handler,
                priority: 5,
            };
            await this.eventBackboneService.subscribe(subscriber);
            logger.info(`Subscribed to time tracking events: ${subscriberName}`);
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to subscribe to time tracking events", {
                subscriberName,
                error,
            });
            return { success: false, error: error instanceof Error ? error.message : "Failed to subscribe", code: "subscription_failed", service: this.name };
        }
    }
    async subscribeToDependencies(handler: (event: MyceliumEvent) => Promise<void> | void, subscriberName: string): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const subscriber: EventSubscriber = {
                name: subscriberName,
                domains: ["task"],
                eventTypes: Object.values(DEPENDENCY_EVENTS),
                handler,
                priority: 5,
            };
            await this.eventBackboneService.subscribe(subscriber);
            logger.info(`Subscribed to dependency events: ${subscriberName}`);
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to subscribe to dependency events", {
                subscriberName,
                error,
            });
            return { success: false, error: error instanceof Error ? error.message : "Failed to subscribe", code: "subscription_failed", service: this.name };
        }
    }
    async subscribeToWorkload(handler: (event: MyceliumEvent) => Promise<void> | void, subscriberName: string): Promise<ServiceResponse<void>> {
        if (!this.initialized || !this.eventBackboneService) {
            return { success: false, error: "MyceliumTimeDependencyConnector not initialized or event backbone not set", code: "service_not_initialized", service: this.name };
        }
        try {
            const subscriber: EventSubscriber = {
                name: subscriberName,
                domains: ["workload"],
                eventTypes: Object.values(WORKLOAD_EVENTS),
                handler,
                priority: 5,
            };
            await this.eventBackboneService.subscribe(subscriber);
            logger.info(`Subscribed to workload events: ${subscriberName}`);
            return { success: true, data: undefined };
        }
        catch (error) {
            logger.error("Failed to subscribe to workload events", {
                subscriberName,
                error,
            });
            return { success: false, error: error instanceof Error ? error.message : "Failed to subscribe", code: "subscription_failed", service: this.name };
        }
    }
}
// ============================================================================
// SINGLETON INSTANCE
// ============================================================================
export const myceliumTimeDependencyConnector = new MyceliumTimeDependencyConnector();
